import { memo, useMemo } from 'react';

import { CalendarIcon, Check, ClockIcon, StickyNote, UserCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Command } from '@/components/ui/command';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import { formatMinutesToTime } from '@/helpers';
import { cn } from '@/lib/utils';
import { EntityAvatar, OpsiumUserShortResponse } from '@/modules/common';
import { formatCompactDateRange } from '@/utils/date-formatters';

import { PLANNER_TIMEOFF_TOOLTIP_WIDTH_PX } from '../../constants';
import { formatTimeOffDays, getTimeOffTotalDays } from '../../helpers/time-off-day-stacking';
import { getDayPartLabelKey } from '../../helpers/time-off-layout';
import { TimeOffBlockType } from '../../types';
import {
  ApprovalStatusDTO,
  DayPortionDTO,
  PlannerTimeoffApproverResponse,
  TimeOffRequestStatusDTO,
} from '../../types/dto/response/planner-timeoff-response';

interface TimeOffTooltipContentProps {
  timeOff: TimeOffBlockType;
  user?: OpsiumUserShortResponse;
  /** Minutes for the hovered segment's day. Drives the per-day hours shown in
   *  the braces. For uniform time-offs this is the shared per-day minutes; for
   *  non-uniform per-day segments it's that specific day's minutes. */
  dayMinutes?: number;
  /** Day portion of the hovered segment — drives the day-part label (e.g.
   *  "First quarter") so the tooltip text matches the UI block. */
  portion?: DayPortionDTO;
  /** Which part of the day the hovered segment covers (1-based). */
  dayPartNumber?: number | null;
}

function IconCell({ icon: Icon }: { icon: typeof CalendarIcon }) {
  return (
    <div className="flex items-center justify-center p-1">
      <Icon size={14} className="flex-shrink-0" />
    </div>
  );
}

/**
 * Vertical-divider list of approvers (mirrors the rate breakdown in the
 * assignment tooltip): each row shows the approver's name on the left and a
 * checkmark on the right once that approver has approved. No avatar — the
 * backend doesn't return approver profile images.
 */
function ApproversList({ approvers }: { approvers: PlannerTimeoffApproverResponse[] }) {
  return (
    <div className="ml-1 flex flex-col gap-1 border-l border-muted-foreground/40 pl-2">
      {approvers.map(approver => (
        <div key={approver.approverId} className="flex items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-sm leading-4">{approver.approverName}</span>
          {approver.status === ApprovalStatusDTO.APPROVED && (
            <Check size={14} className="flex-shrink-0 text-emerald-500" />
          )}
        </div>
      ))}
    </div>
  );
}

export const TimeOffTooltipContent = memo(
  ({ timeOff, user, dayMinutes, portion, dayPartNumber }: TimeOffTooltipContentProps) => {
    const { t } = useTranslation('authenticated.planner');

    const isPending = timeOff.status === TimeOffRequestStatusDTO.PENDING;

    const approvers = timeOff.originalTimeOff.approvers;
    const approvedCount = useMemo(
      () => approvers.filter(a => a.status === ApprovalStatusDTO.APPROVED).length,
      [approvers]
    );

    const dateRangeString = useMemo(
      () =>
        formatCompactDateRange(timeOff.originalTimeOff.startDate, timeOff.originalTimeOff.endDate),
      [timeOff.originalTimeOff.startDate, timeOff.originalTimeOff.endDate]
    );

    const days = timeOff.originalTimeOff.days;

    // Hours row: total time-off expressed as fractional working days (summed
    // across all days, e.g. 3 quarter-days = 0.75 day), with the per-day hours
    // inside the braces. When the hovered segment's day minutes are provided
    // (non-uniform per-day segment), show that day's hours; otherwise fall back
    // to the uniform per-day average.
    const totalDays = useMemo(() => formatTimeOffDays(getTimeOffTotalDays(days)), [days]);
    const totalDaysString = t('timeOff.totalDays', { count: totalDays });
    const perDayMinutes =
      dayMinutes ?? (days.length > 0 ? Math.round(timeOff.totalMinutes / days.length) : 0);
    const perDayString = t('timeOff.perDay', { time: formatMinutesToTime(perDayMinutes) });
    const hoursString = `${totalDaysString} ${perDayString}`;

    // Day-part of the hovered segment (e.g. "First quarter") so the tooltip text
    // matches the visible block. Shown for every portion (Full day / Hourly too).
    const dayPartString = portion ? t(getDayPartLabelKey(portion, dayPartNumber ?? null)) : null;

    return (
      <Command style={{ maxWidth: PLANNER_TIMEOFF_TOOLTIP_WIDTH_PX }}>
        <div className="flex flex-col gap-3 p-3">
          {/* User avatar + name and time-off type */}
          <div className="flex items-center gap-2">
            <EntityAvatar
              image={user?.profileImageUrl}
              name={user?.name}
              size="sm"
              shape="rounded"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-medium leading-5">{user?.name}</p>
              <div className="mt-0.5 flex min-w-0 items-center gap-1">
                <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-time-off-indicator" />
                <p className="truncate text-sm font-medium leading-none text-time-off-text">
                  {timeOff.title}
                </p>
              </div>
            </div>
          </div>

          {/* Details rows */}
          <div className="flex flex-col gap-1">
            {/* Status + approvers */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <IconCell icon={UserCheck} />
                <p
                  className={cn(
                    'text-sm leading-4',
                    isPending ? 'text-time-off-text' : 'text-emerald-600 dark:text-emerald-400'
                  )}
                >
                  {isPending ? t('timeOff.pending') : t('timeOff.approved')}
                </p>
                {approvers.length > 0 && (
                  <span className="text-sm leading-4 text-muted-foreground">
                    {t('timeOff.approvedSummary', {
                      approved: approvedCount,
                      total: approvers.length,
                    })}
                  </span>
                )}
              </div>
              {approvers.length > 0 && <ApproversList approvers={approvers} />}
            </div>

            {/* Date range */}
            <div className="flex items-center gap-1.5">
              <IconCell icon={CalendarIcon} />
              <p className="text-sm leading-4">{dateRangeString}</p>
            </div>

            {/* Hours: total days + hours per day, with the hovered day-part */}
            <div className="flex items-center gap-1.5">
              <IconCell icon={ClockIcon} />
              <p className="text-sm leading-4">
                {hoursString}
                {dayPartString && (
                  <span className="text-muted-foreground before:mx-1 before:content-['·']">
                    {dayPartString}
                  </span>
                )}
              </p>
            </div>

            {/* Description (optional) */}
            {timeOff.originalTimeOff.description && (
              <div className="flex max-w-56 items-center gap-1.5 overflow-hidden">
                <IconCell icon={StickyNote} />
                <TruncatedTooltip
                  content={timeOff.originalTimeOff.description}
                  className="text-sm"
                />
              </div>
            )}
          </div>
        </div>
      </Command>
    );
  }
);

TimeOffTooltipContent.displayName = 'TimeOffTooltipContent';
